import { SUPPORTED_CURRENCIES } from '../../constants/currencies';

const currencyOptions = SUPPORTED_CURRENCIES.map((c) => ({ label: c, value: c }));
const opts = (values) => values.map((v) => ({ label: v.charAt(0).toUpperCase() + v.slice(1).replace(/_/g, ' '), value: v }));

/**
 * Phase 15 — a static mirror of the real Lead/CrmContact/Account/Deal
 * Mongoose schemas (src/modules/crm/models/*.js), trimmed to fields a form
 * builder would actually drop onto a canvas. Internal/system fields
 * (companyId, ownerId, audit trail, soft-delete, relationship arrays like
 * Account.contacts) are deliberately excluded — this is a field picker, not
 * a schema dump.
 *
 * Each entry becomes a fully-configured form component when dragged in —
 * the right input type, enum options pre-filled, label matching the CRM
 * field. `crmObject`/`crmKey` ride along on the component so the CRM
 * Mapping panel can auto-wire it without the user re-selecting anything.
 * Lead/Contact/Account/Deal/Task fields all write back on submission
 * (Phase 15/16/17/23) — Deal's `stage` field is the one exception: it
 * renders for display but the admin fixes a Pipeline + Stage in the CRM
 * Mapping panel instead, since a public submission can't be trusted to
 * name a valid pipeline stage.
 */
export const CRM_FIELD_GROUPS = [
  {
    object: 'lead',
    label: 'Lead',
    fields: [
      { key: 'name', label: 'Lead Name', type: 'text' },
      { key: 'phoneNumber', label: 'Phone', type: 'phone' },
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'source', label: 'Lead Source', type: 'select', options: opts(['whatsapp', 'web_form', 'manual', 'bulk_import', 'instagram', 'api', 'referral', 'conversation_flow', 'google_ads', 'meta_ads', 'tiktok_ads', 'linkedin_ads', 'lead_scraper']) },
      { key: 'status', label: 'Lead Status', type: 'select', options: opts(['new', 'contacted', 'qualified', 'unqualified', 'converted', 'lost']) },
      { key: 'leadQuality', label: 'Lead Quality', type: 'select', options: opts(['low', 'medium', 'high', 'hot']) },
      { key: 'lifecycleStage', label: 'Lifecycle Stage', type: 'select', options: opts(['subscriber', 'lead', 'mql', 'sql', 'opportunity', 'customer', 'evangelist', 'other']) },
      { key: 'expectedValue', label: 'Expected Value', type: 'number' },
      { key: 'currency', label: 'Currency', type: 'select', options: currencyOptions },
      { key: 'website', label: 'Website', type: 'url' },
      { key: 'location', label: 'Location', type: 'text' },
      { key: 'businessType', label: 'Business Type', type: 'text' },
      { key: 'utmSource', label: 'UTM Source', type: 'text' },
      { key: 'utmMedium', label: 'UTM Medium', type: 'text' },
      { key: 'utmCampaign', label: 'UTM Campaign', type: 'text' },
      { key: 'tags', label: 'Tags', type: 'text', placeholder: 'Comma-separated' },
      { key: 'lostReason', label: 'Lost Reason', type: 'textarea' },
    ],
  },
  {
    object: 'contact',
    label: 'Contact',
    fields: [
      { key: 'name', label: 'Contact Name', type: 'text' },
      { key: 'phoneNumber', label: 'Phone', type: 'phone' },
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'company', label: 'Company', type: 'text' },
      { key: 'designation', label: 'Job Title', type: 'text' },
      { key: 'website', label: 'Website', type: 'url' },
      { key: 'street', label: 'Street Address', type: 'text' },
      { key: 'city', label: 'City', type: 'text' },
      { key: 'state', label: 'State', type: 'text' },
      { key: 'country', label: 'Country', type: 'text' },
      { key: 'zip', label: 'ZIP / Postal Code', type: 'text' },
      { key: 'birthday', label: 'Birthday', type: 'date' },
      { key: 'lifecycleStage', label: 'Lifecycle Stage', type: 'select', options: opts(['subscriber', 'lead', 'mql', 'sql', 'opportunity', 'customer', 'evangelist', 'other']) },
      { key: 'preferredChannel', label: 'Preferred Contact Channel', type: 'select', options: opts(['whatsapp', 'email', 'call', 'instagram']) },
      { key: 'doNotContact', label: 'Do Not Contact', type: 'checkbox' },
      { key: 'tags', label: 'Tags', type: 'text', placeholder: 'Comma-separated' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
  {
    object: 'account',
    label: 'Account',
    fields: [
      { key: 'name', label: 'Account Name', type: 'text', required: true },
      { key: 'industry', label: 'Industry', type: 'text' },
      { key: 'website', label: 'Website', type: 'url' },
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'phone', label: 'Phone', type: 'phone' },
      { key: 'street', label: 'Street Address', type: 'text' },
      { key: 'city', label: 'City', type: 'text' },
      { key: 'state', label: 'State', type: 'text' },
      { key: 'country', label: 'Country', type: 'text' },
      { key: 'zip', label: 'ZIP / Postal Code', type: 'text' },
      { key: 'gstin', label: 'GSTIN', type: 'text' },
      { key: 'currency', label: 'Currency', type: 'select', options: currencyOptions },
      { key: 'annualRevenue', label: 'Annual Revenue', type: 'number' },
      { key: 'employees', label: 'Employee Count', type: 'number' },
      { key: 'status', label: 'Account Status', type: 'select', options: opts(['active', 'inactive', 'prospect']) },
      { key: 'tags', label: 'Tags', type: 'text', placeholder: 'Comma-separated' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
  {
    object: 'deal',
    label: 'Deal / Opportunity',
    fields: [
      { key: 'title', label: 'Deal Title', type: 'text', required: true },
      { key: 'value', label: 'Deal Value', type: 'number' },
      { key: 'currency', label: 'Currency', type: 'select', options: currencyOptions },
      { key: 'stage', label: 'Stage', type: 'text' },
      { key: 'probability', label: 'Win Probability (%)', type: 'number' },
      { key: 'expectedCloseDate', label: 'Expected Close Date', type: 'date' },
      { key: 'status', label: 'Deal Status', type: 'select', options: opts(['open', 'won', 'lost', 'on_hold']) },
      { key: 'winReasonCategory', label: 'Win Reason', type: 'select', options: opts(['Competitive Win', 'Better Pricing', 'Better Product Fit', 'Relationship', 'Other']) },
      { key: 'lostReasonCategory', label: 'Loss Reason', type: 'select', options: opts(['Budget', 'Timing', 'Competitor', 'No Response', 'Other']) },
      { key: 'competitorName', label: 'Competitor Name', type: 'text' },
      { key: 'tags', label: 'Tags', type: 'text', placeholder: 'Comma-separated' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
  {
    object: 'task',
    label: 'Task',
    fields: [
      { key: 'title', label: 'Task Title', type: 'text', required: true },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'type', label: 'Task Type', type: 'select', options: opts(['call', 'email', 'whatsapp', 'meeting', 'follow_up', 'custom', 'note', 'other', 'technical']) },
      { key: 'priority', label: 'Priority', type: 'select', options: opts(['low', 'medium', 'high', 'urgent']) },
      { key: 'dueDate', label: 'Due Date', type: 'date' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
];

export function findCrmField(object, key) {
  const group = CRM_FIELD_GROUPS.find((g) => g.object === object);
  return group?.fields.find((f) => f.key === key) || null;
}
